import { useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import {
  COLLECTIONS,
  firebaseDB,
  readTimeFormatMirrorUid,
  readTimeZoneDisplayMirrorUid,
  useAppDispatch,
  useAppSelector,
  useAppStore,
  userActions,
} from "@lemonadetraining/web-shared";
import {
  DEFAULT_TIME_ZONE_DISPLAY,
  isTimeFormatPreference,
  isTimeZoneDisplayPreference,
} from "@lemonadetraining/contracts";

const recognised = <T>(
  value: unknown,
  isValid: (v: unknown) => v is T,
  fallback: T,
  label: string
): T => {
  if (isValid(value)) return value;
  if (value !== undefined) console.warn(`Ignoring unrecognised ${label} preference`);
  return fallback;
};

/**
 * Syncs both the time format and the zone display mode from one read; the
 * phone's saved zone in the same document is never read into the store.
 *
 * Mounted in `apps/app` only — the marketing site renders no times, and the
 * shared auth context both apps mount would fetch this for nothing. Kept out of
 * `loadUserInfoData`, which re-runs on every user-document write.
 *
 * A mirror belonging to someone else is discarded before the read, not after:
 * only sign-out clears it, and the read itself can fail. A field the user
 * changed while the read was in flight keeps the newer choice.
 */
export function useTimeFormatSync(): void {
  const uid = useAppSelector((state) => state.user.user?.uid);
  const dispatch = useAppDispatch();
  const store = useAppStore();

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;

    if (readTimeFormatMirrorUid() !== uid) {
      dispatch(userActions.setTimeFormat("system"));
    }
    if (readTimeZoneDisplayMirrorUid() !== uid) {
      dispatch(userActions.setTimeZoneDisplay(DEFAULT_TIME_ZONE_DISPLAY));
    }

    const atStart = store.getState().user;
    const startFormat = atStart.timeFormat;
    const startMode = atStart.timeZoneDisplay;

    const load = async () => {
      try {
        const snapshot = await getDoc(
          doc(firebaseDB, COLLECTIONS.Users, uid, COLLECTIONS.UserSettings, "preferences")
        );
        if (cancelled) return;

        const data = snapshot.exists() ? snapshot.data() : undefined;
        const now = store.getState().user;

        const format = recognised(
          data?.timeFormat,
          isTimeFormatPreference,
          "system",
          "time-format"
        );
        if (now.timeFormat === startFormat) dispatch(userActions.setTimeFormat(format));

        const mode = recognised(
          data?.timeZoneDisplay,
          isTimeZoneDisplayPreference,
          DEFAULT_TIME_ZONE_DISPLAY,
          "time-zone display"
        );
        if (now.timeZoneDisplay === startMode) dispatch(userActions.setTimeZoneDisplay(mode));
      } catch (error) {
        if (cancelled) return;
        // A failed preference read must never block or degrade the dashboard.
        console.warn("Could not load preferences", (error as { code?: string })?.code);
      }
      dispatch(userActions.setPreferencesSynced(true));
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [uid, dispatch, store]);
}
